import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TransferRow } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { spotLabel } from '../lib/format';

/**
 * "Přidělit převoz" — the three per-row decisions on Převozy, live.
 *
 * Řidič, Cílové stání and GPS are the three cells the legacy driving page
 * had as <select>s with a » button at the end of the row, and they are the
 * same three cells here, in the same order, with the same button. The
 * options are the legacy's: the yard spots for a run in from Europort, the
 * Europort floors for a run out, units 1–5 for the GPS. Nothing is saved
 * until » is pressed; a row with a driver but no target says so instead of
 * sending half a decision.
 *
 * On success every query is refetched; the row leaves the pending queue
 * (it is dispatched now) and the "Rozdělené vozy podle řidiče" chips pick
 * the driver up. A refusal is shown in the server's words under the row's
 * cells — "Převoz na letiště potřebuje patro Europortu" is the whole
 * explanation.
 *
 * Renders three <td>s, so the row's column count is untouched.
 */
export function DispatchCells({ r, gpsClash }: { r: TransferRow; gpsClash: Set<number> }) {
  const qc = useQueryClient();
  const [driverId, setDriverId] = useState<string>(r.assignedUserId ? String(r.assignedUserId) : '');
  const [target, setTarget] = useState<string>(r.parkingTarget ?? '');
  const [gps, setGps] = useState<string>(r.gps == null ? '' : String(r.gps));

  const staff = useQuery({ queryKey: ['catalog', 'staff'], queryFn: () => api.staff(), staleTime: 10 * 60_000 });
  const spots = useQuery({ queryKey: ['catalog', 'spots'], queryFn: () => api.spots(), staleTime: 10 * 60_000 });

  const m = useMutation({
    mutationFn: () =>
      api.dispatch(r.reservationId, {
        direction: r.direction,
        driverId: Number(driverId),
        target,
        gps: gps === '' ? null : Number(gps),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries();
    },
  });

  const ready = driverId !== '' && target.trim() !== '';
  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ??
        (err.code === 'writes_disabled'
          ? 'Zápisy jsou na tomto serveru vypnuté.'
          : err.code === 'forbidden'
            ? 'Na tuhle akci nemáte oprávnění.'
            : err.message)
      : err instanceof Error
        ? err.message
        : null;

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const gpsBad = gps !== '' && gpsClash.has(Number(gps)) && Number(gps) !== r.gps;

  // The yard list for a run in; the Europort floors for a run out. A target
  // already on the row that the list does not know (an old label) is kept
  // as its own option so the select does not silently blank it.
  const targets: Array<{ value: string; label: string }> =
    r.direction === 'e2t'
      ? (spots.data?.tuchomerice ?? []).map((v) => ({ value: v, label: v }))
      : (spots.data?.europort ?? []);
  if (target && !targets.some((t) => t.value === target)) {
    targets.unshift({ value: target, label: spotLabel(target) });
  }

  return (
    <>
      <td className="sel" onClick={stop} onKeyDown={stop}>
        <select
          className="dsp"
          value={driverId}
          onChange={(e) => setDriverId(e.target.value)}
          aria-label="Řidič"
          disabled={m.isPending}
        >
          <option value="">— řidič —</option>
          {(staff.data ?? []).map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.name}
            </option>
          ))}
        </select>
      </td>
      <td className="sel" onClick={stop} onKeyDown={stop}>
        <select
          className="dsp"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          aria-label="Cílové stání"
          disabled={m.isPending}
        >
          <option value="">— kam —</option>
          {targets.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        {message && (
          <div className="arrive-error" role="alert">
            {message}
          </div>
        )}
      </td>
      <td className="gps dspgps" onClick={stop} onKeyDown={stop}>
        <select
          className={gpsBad ? 'dsp bad' : 'dsp'}
          value={gps}
          onChange={(e) => setGps(e.target.value)}
          aria-label="GPS jednotka"
          title={gpsBad ? 'Tuto jednotku má přiřazenou i jiný řidič.' : undefined}
          disabled={m.isPending}
        >
          <option value="">—</option>
          {[1, 2, 3, 4, 5].map((n) => (
            <option key={n} value={String(n)}>
              {n}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn sm primary go"
          disabled={!ready || m.isPending}
          title={ready ? 'Přidělit převoz' : 'Vyberte řidiče a cílové stání'}
          onClick={() => {
            if (ready && !m.isPending) m.mutate();
          }}
        >
          {m.isPending ? '…' : '»'}
        </button>
      </td>
    </>
  );
}
